import { useEffect, useRef, useState, type ReactNode } from 'react';
import { PasswordModal } from '../auth/AccountModals';
import { SessionExpiredModal, SignInScreen } from '../auth/SignIn';
import { AuthProvider, useAuth } from '../auth/AuthProvider';
import { CalculatorView } from '../features/calculator/CalculatorView';
import { ClientProfileView } from '../features/clients/ClientProfileView';
import { ClientsView } from '../features/clients/ClientsView';
import { ComplianceView } from '../features/compliance/ComplianceView';
import { Dashboard } from '../features/dashboard/Dashboard';
import { TechnicianReportsView } from '../features/dashboard/TechnicianReportsView';
import { ServiceMapView } from '../features/map/ServiceMapView';
import { RecordsView } from '../features/records/RecordsView';
import { RodentPlannerView } from '../features/rodent/RodentPlannerView';
import { RunSheetsView } from '../features/runsheets/RunSheetsView';
import { ScheduleView } from '../features/schedule/ScheduleView';
import { TechniciansView } from '../features/technicians/TechniciansView';
import { UsersView } from '../features/users/UsersView';
import { importLegacyBrowserData } from '../data/legacy-import';
import { matchPath, navigate, usePathname } from '../lib/router';
import { useSyncStatus } from '../sync/status';
import { UIProvider } from '../ui/UIProvider';
import { Brand } from './Brand';
import { ModalHost } from './ModalHost';
import { OfflineStatus } from './OfflineStatus';
import { ROUTES, type RouteDef, type ViewId } from './routes';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { UpdatePrompt } from './UpdatePrompt';

export function App() {
  return (
    <AuthProvider>
      <UIProvider>
        <Root />
      </UIProvider>
    </AuthProvider>
  );
}

function Root() {
  const { status, user, passwordOpen } = useAuth();
  const { syncing } = useSyncStatus();
  // Retried after each sync until the device has the server's data to compare against.
  useEffect(() => { if (user && !syncing) importLegacyBrowserData(user).catch((error) => console.warn('Legacy data import skipped', error)); }, [user, syncing]);

  if (status === 'loading') return <div className="app-loading" aria-busy="true"><Brand />Loading…</div>;
  return (
    <>
      {user ? <Shell /> : <SignInScreen />}
      {user && status === 'signed-out' && <SessionExpiredModal />}
      {user && passwordOpen && <PasswordModal />}
      <UpdatePrompt />
    </>
  );
}

function resolveRoute(pathname: string, role: string): { route: RouteDef; params: Record<string, string> } {
  for (const route of ROUTES) {
    const params = matchPath(route.path, pathname);
    if (params) return route.roles && !route.roles.includes(role as never) ? { route: ROUTES.find((item) => item.view === (role === 'tech' ? 'run-sheets' : 'dashboard'))!, params: {} } : { route, params };
  }
  return { route: ROUTES[0], params: {} };
}

const VIEW_COMPONENTS: Record<ViewId, (props: { active: boolean; params: Record<string, string> }) => ReactNode> = {
  dashboard: () => <Dashboard />,
  'technician-reports': () => <TechnicianReportsView />,
  clients: () => <ClientsView />,
  'client-profile': ({ params }) => <ClientProfileView clientId={params.id} />,
  schedule: () => <ScheduleView />,
  'run-sheets': () => <RunSheetsView />,
  map: ({ active }) => <ServiceMapView active={active} />,
  rodent: ({ active }) => <RodentPlannerView active={active} />,
  calculator: () => <CalculatorView />,
  technicians: () => <TechniciansView />,
  documents: () => <RecordsView />,
  compliance: () => <ComplianceView />,
  users: () => <UsersView />
};

function Shell() {
  const { user } = useAuth();
  const pathname = usePathname();
  const { route, params } = resolveRoute(pathname, user!.role);
  const [menuOpen, setMenuOpen] = useState(false);
  const [visited, setVisited] = useState<Set<ViewId>>(() => new Set());
  const sidebarRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Redirect disallowed or unknown paths to the view actually shown.
  useEffect(() => { if (!matchPath(route.path, pathname)) navigate(route.path, { replace: true }); }, [route.path, pathname]);
  useEffect(() => { if (route.keepAlive && !visited.has(route.view)) setVisited((current) => new Set(current).add(route.view)); }, [route, visited]);
  useEffect(() => { document.title = `${route.title} · PCS Internal`; }, [route.title]);
  // A tap outside the mobile navigation closes it.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (sidebarRef.current?.contains(target) || menuButtonRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointer, true);
    return () => document.removeEventListener('pointerdown', onPointer, true);
  }, [menuOpen]);

  const keepAlive = ROUTES.filter((item) => item.keepAlive && (visited.has(item.view) || item.view === route.view));
  return (
    <>
      <Sidebar activeNav={route.nav} open={menuOpen} onClose={() => setMenuOpen(false)} sidebarRef={sidebarRef} />
      {menuOpen && <div className="sidebar-scrim" aria-hidden="true" />}
      <main className="main">
        <OfflineStatus />
        <Topbar section={route.section} title={route.title} onMenu={() => setMenuOpen((value) => !value)} menuButtonRef={menuButtonRef} />
        {!route.keepAlive && <ViewFrame key={route.view + JSON.stringify(params)} route={route} params={params} active />}
        {keepAlive.map((item) => <ViewFrame key={item.view} route={item} params={{}} active={item.view === route.view} />)}
      </main>
      <ModalHost />
    </>
  );
}

function ViewFrame({ route, params, active }: { route: RouteDef; params: Record<string, string>; active: boolean }) {
  return <>{VIEW_COMPONENTS[route.view]({ active, params })}</>;
}
